import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import DateFilter from '../filters/DateFilter';
import { MAX_ALARM_INTEGER, getAlarmRuleCriteriaErrors } from './alarm-rule-validation';

// Debounces text-input commits upstream (mirrors DateFilter's data-window input) so typing
// isn't gated by the host AnalysisController's digest cycle on every keystroke.
const useDebouncedTextInput = (value, onCommit, delay = 300) => {
  const timerRef = useRef(null);
  const latestRef = useRef(value ?? '');
  const lastCommittedRef = useRef(value ?? '');
  const [inputValue, setInputValue] = useState(value ?? '');

  const commit = inputValueToCommit => {
    if (String(inputValueToCommit) === String(lastCommittedRef.current)) return;
    lastCommittedRef.current = inputValueToCommit;
    onCommit(inputValueToCommit);
  };

  const onChange = event => {
    const nextValue = event.target.value;
    latestRef.current = nextValue;
    setInputValue(nextValue);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => commit(latestRef.current), delay);
  };

  const onBlur = () => {
    clearTimeout(timerRef.current);
    commit(latestRef.current);
  };

  useEffect(() => {
    const incoming = value ?? '';
    if (String(incoming) === String(lastCommittedRef.current)) return;
    lastCommittedRef.current = incoming;
    latestRef.current = incoming;
    setInputValue(incoming);
  }, [value]);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  return { inputValue, onChange, onBlur };
};

const CreateAlarmRule = ({
  hasAccessAlarms = false,
  startDate,
  endDate,
  mode,
  value,
  units,
  defaultWindowHours,
  onStartDateChange,
  onEndDateChange,
  onModeChange,
  onValueChange,
  onReset,
  onInitializeRange,
  occurrences,
  intervalValue,
  intervalUnit,
  intervalUnits,
  onOccurrencesChange,
  onIntervalValueChange,
  onIntervalUnitChange,
  isIntervalLongerThanDataWindow,
  isCreateRuleDisabled,
  isRunReportDisabled,
  createRuleDisabledReason,
  runReportDisabledReason,
  onCreateRule,
  onRunReport,
}) => {
  const occurrencesInput = useDebouncedTextInput(occurrences, onOccurrencesChange);
  const intervalValueInput = useDebouncedTextInput(intervalValue, onIntervalValueChange);
  const criteriaErrors = getAlarmRuleCriteriaErrors({
    alarmThresholdCount: occurrencesInput.inputValue,
    alarmIntervalValue: intervalValueInput.inputValue,
    alarmIntervalUnit: intervalUnit,
  });
  const thresholdError = String(occurrencesInput.inputValue).trim() !== '' ? criteriaErrors.alarmThresholdCount : '';
  const intervalError = (String(intervalValueInput.inputValue).trim() !== '' ? criteriaErrors.alarmIntervalValue : '')
    || (isIntervalLongerThanDataWindow ? 'Must not exceed the data window.' : '');
  const invalidCriteria = !!criteriaErrors.alarmThresholdCount || !!criteriaErrors.alarmIntervalValue;

  return (
  <div className="analysis-create-rule-container">
    <div className="analysis-create-rule-container__field analysis-create-rule-container__field--window">
      <div className="chartControlTitle">Data window</div>
      <DateFilter
        startDate={startDate}
        endDate={endDate}
        mode={mode}
        value={value}
        units={units}
        defaultWindowHours={defaultWindowHours}
        onStartDateChange={onStartDateChange}
        onEndDateChange={onEndDateChange}
        onModeChange={onModeChange}
        onValueChange={onValueChange}
        onReset={onReset}
        onInitializeRange={onInitializeRange}
      />
    </div>

    {hasAccessAlarms && <div className="analysis-create-rule-container__occurance-time-interval-and-buttons" data-testid="analysis-alarm-controls">
      <div className="analysis-create-rule-container__threshold-and-within-filters-container">
        <div className="analysis-create-rule-container__field validation-callout-anchor">
          <div className="chartControlTitle" title="How many matching event occurrences are required to trigger an alarm, e.g. 3">Threshold Count</div>
          <label className="sr-only" htmlFor="analysisCreateRuleOccurrences">Threshold Count</label>
          <input
            type="number"
            className="form-control analysis-create-rule-container__input"
            id="analysisCreateRuleOccurrences"
            value={occurrencesInput.inputValue}
            min="1"
            max={MAX_ALARM_INTEGER}
            step="1"
            aria-invalid={!!thresholdError}
            aria-describedby={thresholdError ? 'create-alarm-rule-threshold-error' : undefined}
            placeholder="e.g. 3"
            title="How many matching event occurrences are required to trigger an alarm, e.g. 3"
            onChange={occurrencesInput.onChange}
            onBlur={occurrencesInput.onBlur}
            data-testid="create-alarm-rule-occurrences-input"
          />
          {thresholdError && <p id="create-alarm-rule-threshold-error" className="validation-callout" role="alert" data-testid="create-alarm-rule-threshold-error">{thresholdError}</p>}
        </div>
      <div className="analysis-create-rule-container__field">
        <div className="chartControlTitle" title="The occurrence window used to count matching events toward the alarm threshold, e.g. 15">Within</div>
          <div className="alarm-rule-form__interval validation-callout-anchor">
            {intervalError && (
              <p id="create-alarm-interval-exceeds-data-window-error" className="validation-callout" role="alert" data-testid="create-alarm-interval-exceeds-data-window-error">
                {intervalError}
              </p>
            )}
            <input
              type="number"
              id="createAlarmIntervalValue"
              className={`form-control${intervalError ? ' analysis-create-rule-container__input--error' : ''}`}
              value={intervalValueInput.inputValue}
              aria-invalid={!!intervalError}
              aria-describedby={intervalError ? 'create-alarm-interval-exceeds-data-window-error' : undefined}
              placeholder="e.g. 15"
              min="0"
              step="any"
              title={intervalError || 'The occurrence window used to count matching events toward the alarm threshold, e.g. 15'}
              onChange={intervalValueInput.onChange}
              onBlur={intervalValueInput.onBlur}
              data-testid="create-alarm-interval-value-input"
            />
            <div className="select-with-chevron">
              <select
                id="createAlarmIntervalUnit"
                className="form-control"
                value={intervalUnit}
                aria-label="Alarm interval time unit"
                title="Time unit for the sliding window"
                onChange={event => onIntervalUnitChange(event.target.value)}
                data-testid="create-alarm-interval-unit-select">
                {intervalUnits.map(unit => <option key={unit.value} value={unit.value}>{unit.label}</option>)}
              </select>
              <i className="fas fa-chevron-down" aria-hidden="true" />
            </div>
          </div>
      </div>
      </div>

      <div className="analysis-create-rule-container__action-buttons">
        <div className="analysis-create-rule-container__report-rule-buttons-container">
        <div className="analysis-create-rule-container__action">
          <div className="chartControlTitle">Create Rule</div>
          <span
            title={createRuleDisabledReason || 'Create an alarm rule from the current filters'}
            data-testid="create-alarm-rule-button-hint">
            <button
              type="button"
              className="btn btn-primary"
              onClick={onCreateRule}
              disabled={isCreateRuleDisabled || invalidCriteria}
              data-testid="create-alarm-rule-button">
              <i className="fas fa-plus" />
            </button>
          </span>
        </div>
        <div className="analysis-create-rule-container__action">
          <div className="chartControlTitle">Run Report</div>
          <span title={runReportDisabledReason || 'Run Report'} data-testid="run-alarm-report-button-hint">
            <button
              type="button"
              className="btn btn-primary"
              onClick={onRunReport}
              disabled={isRunReportDisabled || invalidCriteria}
              data-testid="run-alarm-report-button">
              <AssessmentOutlinedIcon fontSize="small" />
            </button>
          </span>
        </div>
        </div>
      </div>
    </div>}
  </div>
  );
};

CreateAlarmRule.propTypes = {
  hasAccessAlarms: PropTypes.bool,
  startDate: PropTypes.object,
  endDate: PropTypes.object,
  mode: PropTypes.string,
  value: PropTypes.number,
  units: PropTypes.array,
  defaultWindowHours: PropTypes.number,
  onStartDateChange: PropTypes.func.isRequired,
  onEndDateChange: PropTypes.func.isRequired,
  onModeChange: PropTypes.func.isRequired,
  onValueChange: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  onInitializeRange: PropTypes.func,
  occurrences: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  intervalValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  intervalUnit: PropTypes.string,
  intervalUnits: PropTypes.array.isRequired,
  onOccurrencesChange: PropTypes.func.isRequired,
  onIntervalValueChange: PropTypes.func.isRequired,
  onIntervalUnitChange: PropTypes.func.isRequired,
  isIntervalLongerThanDataWindow: PropTypes.bool,
  isCreateRuleDisabled: PropTypes.bool,
  isRunReportDisabled: PropTypes.bool,
  createRuleDisabledReason: PropTypes.string,
  runReportDisabledReason: PropTypes.string,
  onCreateRule: PropTypes.func.isRequired,
  onRunReport: PropTypes.func.isRequired,
};

export default CreateAlarmRule;
